<template lang="html">
  <article
    class="group product-card flex h-full flex-col overflow-hidden motion-reduce:transition-none"
    :class="isMinimal
      ? 'bg-transparent'
      : 'rounded-md border border-default bg-default transition-shadow focus-within:ring-2 focus-within:ring-primary hover:border-primary/40 hover:shadow-xs'"
  >
    <!-- Media: square catalog frame (DNS-style) -->
    <div
      class="product-card__media relative aspect-square overflow-hidden"
      :class="isMinimal ? 'bg-transparent' : 'bg-elevated'"
    >
      <NuxtLink
        :to="link"
        class="absolute inset-0 block"
        :aria-label="productName"
      >
        <UiPicture
          key="cover"
          class="product-card__img size-full transition-transform duration-300 group-hover:scale-[1.03] motion-reduce:transition-none motion-reduce:group-hover:scale-100 [&>img]:object-contain"
          :src="coverId"
          variant="imageFull"
          :alt="productName"
          :sizes="CARD_SIZES"
          :loading="props.eager ? undefined : 'lazy'"
        />

        <UiPicture
          v-if="sideCoverId && !isMinimal"
          key="side-cover"
          class="product-card__img absolute inset-0 size-full opacity-0 transition-opacity duration-200 group-hover:opacity-100 motion-reduce:transition-none [&>img]:object-contain"
          :src="sideCoverId"
          variant="imageFull"
          :alt="productName"
          :sizes="CARD_SIZES"
          loading="lazy"
        />
      </NuxtLink>

      <div
        v-if="!isInStock && !isMinimal"
        class="pointer-events-none absolute inset-0 z-[1] bg-default/55"
        aria-hidden="true"
      />

      <div
        v-if="!isMinimal"
        class="pointer-events-none absolute inset-x-0 top-0 z-10 flex flex-col items-start gap-1 p-2"
      >
        <PromoBadge
          :badge="props.value.promoBadge"
          :cart-promo="props.value.hasCartPromo && !props.value.hasCashbackPromo"
          :cashback="props.value.hasCashbackPromo"
        />
        <span
          v-if="!isInStock"
          class="rounded-md bg-tertiary-500 px-2 py-0.5 text-xs font-medium text-white"
        >
          Нет в наличии
        </span>
      </div>
    </div>

    <!-- Minimal: price then title (search popover) -->
    <div
      v-if="isMinimal"
      class="product-card__body flex min-h-0 flex-col gap-1 pt-2"
    >
      <PromoPrice
        :value="props.value"
        class="min-w-0 text-sm font-semibold text-highlighted"
      />
      <NuxtLink
        :to="link"
        class="line-clamp-2 text-xs leading-snug text-muted transition-colors hover:text-primary focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none motion-reduce:transition-none"
      >
        {{ productName }}
      </NuxtLink>
    </div>

    <div
      v-else
      class="product-card__body flex min-h-0 flex-1 flex-col gap-2 p-2.5 sm:p-3"
    >
      <NuxtLink
        :to="link"
        class="line-clamp-3 text-sm leading-snug text-highlighted transition-colors hover:text-primary focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none motion-reduce:transition-none"
      >
        {{ productName }}
      </NuxtLink>

      <!-- Price + actions: wrap when the card is too narrow for one row -->
      <div class="mt-auto flex flex-wrap items-end gap-2">
        <PromoPrice
          :value="props.value"
          class="text-base"
        />

        <div class="ms-auto flex shrink-0 items-center gap-1.5">
          <NuxtLink
            :to="link"
            class="product-card__action flex size-8 items-center justify-center rounded-md border border-default text-muted transition-colors hover:border-primary hover:text-primary focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none motion-reduce:transition-none sm:size-9"
            aria-label="Подробнее о товаре"
          >
            <UIcon
              name="i-lucide-info"
              class="size-3.5 sm:size-4"
            />
          </NuxtLink>

          <button
            type="button"
            class="product-card__action flex size-8 items-center justify-center rounded-md border border-default transition-colors hover:border-primary hover:text-primary focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none motion-reduce:transition-none sm:size-9"
            :class="isFavorite ? 'border-error/40 text-error' : 'text-muted'"
            :aria-label="isFavorite ? 'Убрать из избранного' : 'В избранное'"
            :aria-pressed="isFavorite"
            @click.stop.prevent="onFavoriteToggle"
          >
            <UIcon
              :name="isFavorite ? 'i-heroicons-heart-solid' : 'i-lucide-heart'"
              class="size-3.5 sm:size-4"
            />
          </button>

          <button
            type="button"
            class="product-card__action flex size-8 items-center justify-center rounded-md border transition-colors focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-60 motion-reduce:transition-none sm:size-9"
            :class="inCart
              ? 'border-primary bg-primary/10 text-primary hover:bg-primary/15'
              : 'border-default text-muted hover:border-primary hover:text-primary'"
            :disabled="cartPending || !isInStock || !props.value.id"
            :aria-label="inCart ? 'В корзине — добавить ещё' : 'В корзину'"
            :aria-pressed="inCart"
            @click.stop.prevent="onAddToCart"
          >
            <UIcon
              v-if="!cartPending"
              name="i-lucide-shopping-cart"
              class="size-3.5 sm:size-4"
            />
            <span
              v-else
              class="size-3.5 animate-spin rounded-full border-2 border-current border-r-transparent sm:size-4"
              aria-hidden="true"
            />
          </button>
        </div>
      </div>
    </div>
  </article>
</template>

<script setup lang="ts">
import type { EntityRef, ProductListItemDto } from '@repo/api/web/models'

import { useEcommerce } from '#Analytics/composables/useEcommerce'
import { mapCatalogProduct } from '#Analytics/utils/ecommerceMapper'
import { useCart } from '#Cart/composables/useCart'
import PromoBadge from '#Promotion/components/PromoBadge.vue'
import PromoPrice from '#Promotion/components/PromoPrice.vue'
import UiPicture from '~/app/components/Image/UiPicture.vue'

const props = withDefaults(
  defineProps<{
    value: ProductListItemDto
    category?: EntityRef
    /** First listing row (LCP candidates) loads eagerly; rest stay lazy */
    eager?: boolean
    /** Search popover: image + price + title only */
    mode?: 'default' | 'minimal'
  }>(),
  {
    eager: false,
    mode: 'default',
  },
)

const isMinimal = computed(() => props.mode === 'minimal')

/** Slot width for NuxtPicture `sizes` in ~240px retail grids */
const CARD_SIZES = '50vw sm:33vw lg:280px'

const { routing } = useProducts()
const { toggle: toggleFavorite } = useFavoritesCore()
const { checkInCart, addToCart } = useCart()
const { send } = useRequest()
const ecommerce = useEcommerce()

const coverId = computed(() => props.value.images?.[0]?.id)
const sideCoverId = computed(() => props.value.images?.[1]?.id)

const productName = computed(
  () => props.value.wellKnownAttributes?.Name || 'Товар',
)
const link = computed(() => routing.getDetails(props.value, props.category).route)
const isInStock = computed(() => Boolean(props.value.inStock))
const model = ref(props.value)

const isFavorite = computed(() => model.value.isFavorite)
const inCart = computed(() => checkInCart(props.value.id))
const cartPending = ref(false)

async function onFavoriteToggle() {
  await toggleFavorite(model.value)
}

async function onAddToCart() {
  if (!props.value.id || !isInStock.value || cartPending.value) return

  cartPending.value = true
  try {
    await send(() => addToCart(props.value.id!), {
      successMessage: 'Товар добавлен в корзину',
    })
    ecommerce.add(mapCatalogProduct(props.value, { quantity: 1 }))
  }
  finally {
    cartPending.value = false
  }
}
</script>
